import { PageShell } from "../../components/service/PageShell";
import { ServiceHero, CtaPrimary, CtaGhost, FeatureGrid, StepsList, FaqBlock, Section, Overline, SectionTitle, Accent, Reveal } from "../../components/service/Blocks";
import { Simulator } from "../../components/Simulator";
import { Testimonials } from "../../components/Testimonials";
import { LeadForm } from "../../components/LeadForm";
import { SITE, waLink, IMAGES } from "../../data/site";
import { Building2, FileCheck, BadgeCheck, Ban, Zap, Users } from "lucide-react";

const FAQ = [
  { q: "Preciso instalar alguma coisa?", a: "Não. A energia vem de usinas parceiras e entra como crédito na sua conta. Nada muda no imóvel, no medidor ou na distribuidora." },
  { q: "Por que duas faturas?", a: "A distribuidora continua cobrando taxa mínima, iluminação pública e impostos; a energia com desconto vem na outra fatura. Somadas, ficam mais baratas que antes." },
  { q: "Quanto tempo até o primeiro desconto?", a: "Em geral, cerca de 90 dias depois do cadastro validado. Não existe prazo único: vale o do seu contrato, que depende da distribuidora." },
  { q: "Qual é o desconto?", a: "Até 15%, conforme a distribuidora e a faixa de consumo. O desconto vale sobre a energia compensada; taxa mínima, iluminação pública, impostos e bandeira não entram." },
  { q: "Como faço para cancelar?", a: "O cancelamento é feito pelo canal de atendimento ao cliente. As faturas continuam chegando até acabar a energia que já foi enviada, porque é energia que você já usou." },
  { q: "Isso é programa do governo?", a: "Não. A iGreen Energy é uma empresa privada, e eu sou um licenciado autorizado dela. Desconfie de quem oferecer isso como programa do governo." },
];

export default function Energia() {
  return (
    <PageShell testid="page-energia">
      <ServiceHero
        testid="energia-hero"
        overline="Conexão Green · energia por assinatura"
        title={["Desconto na conta de luz", "sem placa no telhado."]}
        sub="A energia vem de usinas solares parceiras e entra como crédito na sua conta. Você paga menos todo mês, sem obra, sem trocar medidor e sem custo de adesão."
        bullets={["Até 15%, conforme a região", "Sem obra", "100% digital", "Sem custo de adesão"]}
        primary={<CtaPrimary href="#simular" external={false} testid="energia-calc-cta">Calcular minha economia</CtaPrimary>}
        secondary={<CtaGhost href="#conectar" external={false} testid="energia-connect-cta">Conectar grátis em 5 minutos</CtaGhost>}
        image={IMAGES.solarField}
      />

      <Section testid="energia-o-que-e">
        <div className="grid lg:grid-cols-2 gap-12">
          <Reveal>
            <Overline>O que é</Overline>
            <SectionTitle>Energia solar <Accent>sem instalar nada</Accent></SectionTitle>
            <p className="mt-5 text-sm sm:text-base text-emerald-100/60 leading-relaxed">
              Usinas solares parceiras geram energia e injetam na rede da distribuidora da sua região. Essa energia vira
              créditos que são abatidos da sua conta, como prevê a Lei 14.300/2022, o marco legal da geração distribuída.
            </p>
            <p className="mt-4 text-sm sm:text-base text-emerald-100/60 leading-relaxed">
              Você continua recebendo a energia do mesmo jeito, pela mesma rede. Nada muda no seu imóvel. A iGreen não é
              a distribuidora e não é programa do governo: é uma empresa privada, e o seu desconto fica escrito no contrato.
            </p>
          </Reveal>
          <div className="grid gap-5">
            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-[#00E676]/25 bg-[#0B261E]/60 p-7">
                <h3 className="font-heading font-bold text-white inline-flex items-center gap-2"><Zap size={17} className="text-[#00E676]" /> Quem pode</h3>
                <ul className="mt-4 space-y-2.5 text-sm text-emerald-100/60 leading-relaxed list-disc pl-5">
                  <li>Casas e empresas ligadas em baixa tensão (a maioria das contas residenciais e comerciais)</li>
                  <li>O titular da conta de luz, pessoa física ou jurídica</li>
                  <li>Contas a partir de cerca de 130 a 200 kWh por mês, conforme a distribuidora e o tipo de ligação</li>
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="rounded-2xl border border-white/10 bg-[#0B261E]/40 p-7">
                <h3 className="font-heading font-bold text-white inline-flex items-center gap-2"><Ban size={17} className="text-red-400/80" /> Quem não entra</h3>
                <ul className="mt-4 space-y-2.5 text-sm text-emerald-100/50 leading-relaxed list-disc pl-5">
                  <li>Quem tem Tarifa Social (baixa renda)</li>
                  <li>Quem já gera energia solar no imóvel</li>
                  <li>Empresas de média e alta tensão (para elas existe a Energia para Empresas)</li>
                  <li>Tarifa Branca e algumas modalidades rurais específicas</li>
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </Section>

      <Section className="bg-[#061A14]" testid="energia-cobranca">
        <Reveal>
          <Overline>Detalhes</Overline>
          <SectionTitle>Como fica a <Accent>cobrança</Accent></SectionTitle>
        </Reveal>
        <div className="mt-12">
          <FeatureGrid
            testid="energia-cobranca-grid"
            items={[
              { icon: Building2, title: "Conta da distribuidora", text: "Continua chegando, bem menor. Nela ficam a taxa mínima, a iluminação pública e os impostos, que não entram no desconto." },
              { icon: FileCheck, title: "Fatura da energia com desconto", text: "Cobra a energia que veio da usina, já com o desconto do seu contrato. Chega por WhatsApp e e-mail; não existe boleto impresso." },
              { icon: BadgeCheck, title: "Em algumas regiões, uma fatura só", text: "Dependendo da distribuidora, a cobrança pode vir num boleto único. Eu te digo qual é o seu caso antes de você assinar." },
            ]}
          />
        </div>
        <p className="mt-6 text-[11px] text-emerald-100/40 leading-relaxed max-w-3xl">
          Os créditos de energia valem por 60 meses (Lei 14.300). Se você cancelar, as faturas continuam chegando até acabar a energia que já foi enviada para a sua conta.
        </p>
      </Section>

      <Section testid="energia-passos">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <Overline>Passo a passo</Overline>
            <SectionTitle>Como você começa a <Accent>economizar</Accent></SectionTitle>
          </Reveal>
          <StepsList
            testid="energia-steps"
            steps={[
              { n: "01", title: "Você me manda a conta de luz", text: "Pelo WhatsApp ou pelo formulário. Eu confiro se a sua região é atendida e qual é o desconto." },
              { n: "02", title: "Você assina pelo celular", text: "Cadastro e contrato 100% digitais. Só o titular da conta assina." },
              { n: "03", title: "A usina começa a injetar créditos", text: "Depois do cadastro validado, a distribuidora registra os créditos. O primeiro desconto costuma aparecer em cerca de 90 dias; o prazo exato depende da sua distribuidora e vem no contrato." },
              { n: "04", title: "Você paga menos todo mês", text: "Você recebe a conta da distribuidora, menor, e a fatura da energia com desconto. Somadas, saem mais baratas que a conta de antes." },
            ]}
          />
        </div>
      </Section>

      <Simulator />

      <Section className="bg-[#061A14]" testid="energia-confiavel">
        <Reveal>
          <Overline>Antes de decidir</Overline>
          <SectionTitle>A iGreen é <Accent>confiável</Accent>?</SectionTitle>
          <p className="mt-4 max-w-2xl text-sm sm:text-base text-emerald-100/55">
            É normal desconfiar de desconto na conta de luz. Veja o que você pode conferir antes de se cadastrar.
          </p>
        </Reveal>
        <div className="mt-12">
          <FeatureGrid
            testid="energia-confiavel-grid"
            items={[
              { icon: Building2, title: "Empresa privada", text: "A iGreen Energy é uma empresa privada, não um programa do governo. Desconfie de quem oferecer isso como programa do governo." },
              { icon: FileCheck, title: "Previsto em lei", text: "A energia de usinas solares parceiras entra como crédito na sua conta. Esse modelo está na Lei 14.300/2022." },
              { icon: BadgeCheck, title: "Tudo no contrato", text: "O desconto fica escrito no contrato, que chega no seu e-mail com o link para assinar." },
              { icon: Zap, title: "Cadastro grátis", text: "O cadastro da conta de luz não tem custo de adesão." },
              { icon: Ban, title: "Sem obra", text: "Nada muda no imóvel, no medidor ou na distribuidora." },
              { icon: Users, title: "Mais de 800 mil clientes", text: "A iGreen soma mais de 800 mil clientes e recebeu o Prêmio Experiência do Cliente do Reclame Aqui em 2025." },
            ]}
          />
        </div>
        <Reveal delay={0.1}>
          <p className="mt-10 text-sm text-emerald-100/60 leading-relaxed max-w-2xl">
            Quem te atende aqui é <strong className="text-white">{SITE.licenciado} · Licenciado Autorizado iGreen Energy (ID {SITE.licenciadoId})</strong>. Pergunte o que quiser antes de decidir.
          </p>
          <div className="mt-6">
            <CtaPrimary href={waLink()} testid="energia-duvida-cta">Tirar minha dúvida no WhatsApp</CtaPrimary>
          </div>
        </Reveal>
      </Section>

      <Testimonials />

      <FaqBlock testid="energia-faq" title={<>Perguntas sobre <Accent>desconto na conta de luz</Accent></>} items={FAQ} />

      <div id="conectar">
        <LeadForm
          overline="Vamos conversar?"
          title={<>Me conta o seu caso e eu te respondo <Accent>no WhatsApp</Accent>.</>}
          subtitle={`Atendimento por ${SITE.licenciado} · Licenciado Autorizado iGreen Energy (ID ${SITE.licenciadoId}).`}
          defaultInterest="Desconto na conta de luz"
        />
      </div>
    </PageShell>
  );
}
